import * as THREE from "three";
import { NativeTypes } from "react-dnd-html5-backend";
import React from "react";
import { MessageModel, MessagePayload } from "@chatscope/chat-ui-kit-react";
import {MessageDirection, MessageType} from "@chatscope/chat-ui-kit-react/src/types/unions";
import {IChatSessionWithId, IMessage} from "common/IChatSession";
import { IMaterialDefinition, IUITemplateDefinition } from "./Assets";
import { IEditorPreferences } from "./Preferences";
import { IDisplayDeviceInfo, IVisageEditorSettings } from "./VisageUI";
import { IWorklinkSettings } from "./Worklink";
import { CameraProjectionModes, TrackerTypes, ObjectInspectorSectionTypes, AssetTypes } from "../globals";

// from https://stackoverflow.com/a/74823834/618726
export type Entries<T> = {
	[K in keyof T]: [K, T[K]];
}[keyof T][];
export const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

// thanks to https://github.com/react-dnd/react-dnd/issues/1305#issuecomment-477923239
export type ReactDnDNativeFileDropItem = { type: typeof NativeTypes.FILE, files: File[] };

export interface IExportedProjectData
{
	name?: string,
	materialData: {
		materials: IMaterialDefinition[],
		textures: string[],
		videos: string[],
	},
	slideMasterData: {
		[key: string]: IUITemplateDefinition,
	},
	extras?: any,
	materials?: any, // old format
	// TODO: add missing types
}

export interface IAuthorInfo
{
	id: string;
	email: string;
	first_name: string;
	last_name: string;
	name: string;
	external_id: number;
	permalink: string;
	username: string;
}

export interface IRect
{	
	left: number,
	top: number,
	right: number,
	bottom: number,
}

export interface IDomRect extends IRect
{
	xMin: number,
	yMin: number,
	xMax: number,
	yMax: number,
	width: number,
	height: number,
}

export const emptyDomRect = () => ({ xMin: 0, yMin: 0, xMax: 0, yMax: 0, width: 0, height: 0, left: 0, top: 0, right: 0, bottom: 0 } as IDomRect);

export interface IVector2
{
	x: number,
	y: number,
}

export interface IVector3 extends IVector2
{
	z: number,
}

export interface IVector4 extends IVector3
{
	w: number,
}

export type IQuaternion = IVector4; // just an alias for semantic reasons; they have the same properties

// IColor defines a color, where RGBA is 0-255
export interface IColor
{
	r: number,
	g: number,
	b: number,
	a?: number,
}

export interface ITooltipDefinition
{
	content: {
		[key: string | number]: JSX.Element;
	}
	keybinding?: () => string | null;
}

export interface IContextMenuItem 
{
	id: string;
	renderObject: React.ReactNode;
	callback?: (...params: any) => void;
	callbackParams?: any[];
	disabled?: boolean;
	noclick?: boolean;
	size?: number;
	nested?: IContextMenuItem[];
	action?: any;
	icon?: React.ReactNode;
	shortcut?: string;
}

export interface IContextMenuGroup {
	items: IContextMenuItem[];
}

export interface IObjectSelection
{
	objects: THREE.Object3D[],
	groupGuid?: string,
	sceneGuid: string,
}

export enum InspectionTargetTypes {
	None,
	Object3D,
	StepUI,
}

export interface IInspectionTarget
{
	type: InspectionTargetTypes,
	target: any,
}

export interface IPublishSettings
{
	publishType: number,
	encrypt: boolean,
	openInInteractiveMode: boolean,
	bypassCPS: boolean,
}

export interface ILinkedObjectData
{
	object: THREE.Object3D,
	parent: THREE.Object3D,
	defaultObject: THREE.Object3D,
	linkedScene: THREE.Scene,
}

export interface ITextVariablesParams
{
	metaData?: IMetaDataObject
	stepId?: string,
	sequenceId?: string,
	partName?: string,
}
export interface INamedObject
{
	name: string;
}

export interface ICardRecord //for card grid
{
	id: string,
	caption: string,
	imgSrc?: string,
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
	onDoubleClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
	tooltip?: string,
	moreInfoUrl?: string,
}

export enum UIVersions {
	none = "n/a",
	old10 = "1.0",
	new20 = "2.0",
}

export interface IUsedStepTemplate
{
	id: string;
	name: string;
	version: UIVersions;
	isDefault: boolean;
}

export enum BuiltInCubeMaps {
	Default = "default",
	Studio = "studio",
	Interior = "Interior",
	Outside = "outside",
}

export interface TracketTypeCount {
	type: string,
	count: number 
}

export interface AssetInfoProp {
	size: number,
	type: AssetTypes,
	name: string,
	id: string,
	is_revision: boolean,
}


interface IAdvancedOptionsMetaData {
	laplaceThreshold: number;
	normalThreshold: number;
	minInlierRatioInit: number;
	minInlierRatioTracking: number;
	lineGradientThreshold: number;
	lineSearchLengthInitRelative: number;
	lineSearchLengthTrackingRelative: number;
	keyFrameDistance: number;
	fovPercent: number;
}

export interface ITrackerMetaData {
	name: string;
	type: string;
	displayMode: string;
	guideImageAssetId: string | undefined;
	guideSize: number;
	autoInit: boolean;
	advancedOptions: IAdvancedOptionsMetaData;
	initPoseCamera: number;
	ghostGuideNodeIndex: number;
}

export interface IProjectMetadata
{
	ui_version: string;
	templates: Array<{ id: string, name: string, version: UIVersions }>;
	step_count: number;
	ar_tracker_types: Array<TrackerTypes>;
	skip_to_interactive_mode: boolean;
	object_types: Array<string>;
	object_count: number;
	poly_count: number;
	model_ar_trackers: ITrackerMetaData[];
	step_versions: { [UIVersions.old10]: number, [UIVersions.new20]: number };
	hdr: BuiltInCubeMaps;
	ar_tracker_type_count: TracketTypeCount;
	asset_info_props: AssetInfoProp[];
}

export interface ISceneViewData {
	windowId: string,
	context: CanvasRenderingContext2D,
	perspectiveCamera: THREE.PerspectiveCamera,
	orthographicCamera: THREE.OrthographicCamera,
	activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
	aspectRatio: number,
	cameraController: any,
	viewCube: any,
	setCameraProjectionMode: (mode: CameraProjectionModes, simulationStarting?: boolean) => void,
	isMainSceneView: boolean,
	renderViewCube: boolean,
}

export enum SortDirections {
	Ascending = "ASC",
	Descending = "DESC",
}

export interface IBoolSelectorOption extends IDropdownOption { }
export interface IDropdownOption {
	/** the visual representation of the dropdown option */
	display: React.ReactNode,
	/** the value that will be set when the user selects this option */
	value: any,
	/** used for searching the dropdown list */
	tag?: string,
	/** the key to the tooltip definition to display when the cursor is over this option */
	tooltip?: string,
	/** when set to true, the option will be disabled and not selectable */
	disabled?: boolean,
}

export interface IClipboard
{
	type: ObjectInspectorSectionTypes,
	data: any,
}

export interface IActionValuesChange
{
	/** the name of the CSS property or Attribute */
	name: string,
	/** this array is equal in length to the componentIds array, with matching indices. stores the old value */
	oldValues: string[],
	/** this array is equal i length to the componentsIds array, with matching indices. stores the new value */
	newValues: string[],
}

export abstract class MessageModelWithGuid implements MessageModel, IMessage
{
	type?: MessageType | undefined;
	payload?: MessagePayload;
	id?: string;
	// If true, the message will be immutable
	final?: boolean;
	details: string[] = []
	completed: number = 0
	total: number = 1
	direction: MessageDirection = "incoming"
	position: "single" | "first" | "normal" | "last" | 0 | 1 | 2 | 3 = "single";
	message?:string;
	statusMessage?:string;
	sentTime?:string;
	sender?:string;
	metadata?: {[key: string]: any}; // Dedicated field for structured data like stored objects/assets
	attachments: string[] = [];
}

export interface IMetaDataProperty
{
	value: string;
	readonly: boolean;
}

export interface IMetaDataObject 
{
	[key: string]: IMetaDataProperty;
}

/** 
 * These are the tags that are supported by TMP that we can also display in the browser 
 * https://docs.unity3d.com/Packages/com.unity.textmeshpro@4.0/manual/RichTextSupportedTags.html
 */
export const SupportedRichTextTags = [
	"align", 			// ="alignment"> possible values are left, center, right, justified, and flush. convert to text-align:value. "flush" combines text-align:justify with text-align-last:justify
	"allcaps", 			// convert to text-transform:uppercase
	"alpha", 			// =#FF> the allowed value is in hex format and needs to be converted to a number between 0 and 1 and replaced with opacity:value
	"b", 				// keep as is for bold text
	"br", 				// keep as is for line breaks
	"color", 			// =#hexhex> or ="color name"> we should use the html color parsing utility and conver to color:parsed-value
	"cspace", 			// letter-spacing:1px or letter-spacing:1em
	"font-weight",		// ="x00"> convert to font-weight:value
	"i", 				// keep as is for italic text
	"line-height", 		// =xyz> possible values in px, em, or %. convert to line-height:value
	"line-indent", 		// =xyz> possible values in px, em, or %. convert to text-indent.we can't support the each-line option because Chrome doesn't support it yet
	"lowercase",		// text-transform:lowercase
	"margin", 			// =xyz> possible values in. should be minimum of 0 because TMP doesn't support negative margins. conver to combined margin-left:value and margin-right:value (does not affect top or bottom)
	"margin-left",  	// same as "margin" but converts to only margin-left
	"margin-right", 	// same as "margin" but converts to only margin-right
	"mark", 			// for highlighting text
	"s", 				// convert to text-decoration:line-through
	"size", 			// =xyz> possible values in px, em, or %. conver to font-size:value. though TMP supports relative px values, we don't know the initial size so we won't accept them
	"smallcaps", 		// convert to font-variant:small-caps
	"strikethrough",	// convert to text-decoration:line-through
	"sub", 				// keep as is for subscript text
	"sup", 				// keep as is for superscript text
	"u", 				// keep as is for underline text
	"underline",		// convert to text-decoration:underline
	"uppercase",		// convert to text-transform:uppercase
];

/**
 * similar to `Partial<T>` but supports nested types  
 * credit to https://norday.tech/posts/2021/typescript-partial/
 * @example 
 */
export type Subset<K> = {
	[attr in keyof K]?: K[attr] extends (infer U)[]
		? Subset<U>[]
		: K[attr] extends readonly (infer U)[]
			? readonly Subset<U>[]
			: K[attr] extends Date | ((...args: any[]) => any) | RegExp
				? K[attr]
				: K[attr] extends object
					? Subset<K[attr]>
					: K[attr];
};

export interface IUserSettings
{
	preferences: IEditorPreferences;
	uiPreview: {
		scale: number;
		aspect: number;
	};
	visageUI: {
		displayDevice: IDisplayDeviceInfo;
		editorSettings: IVisageEditorSettings;
	};
	view: {
		spritesVisible: boolean;
		trackersVisible: boolean;
		gridVisible: boolean;
		uiVisible: boolean;
	};
	publishSettings: IPublishSettings;
	worklink: IWorklinkSettings;
	workspace: any;
}